import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import {
  SelectInput,
  NumberInput,
} from '../Inputs/Inputs'
import RedX from '../Graphics/RedX'
import AppDispatcher from '../../../dispatchers/AppDispatcher'

import styles from '../../../../css/settings.module.css'

const PRESSURE_AFTER_VALUEKEY = 'AN2'
const PRESSURE_BEFORE_VALUEKEY = 'AN1'

const MAX_PRESSURE = 200

function PumpPressure (props) {
  const {
    device: {
      physical: {
        anIn1Label = 'Analog Input 1',
        anIn2Label = 'Analog Input 2',
        r1FLabel = 'Relay Feedback 1',
        r2FLabel = 'Relay Feedback 2',
        anIn1Type = 'current4To20',
        anIn2Type = 'current4To20',
        r1FType = 'closure',
        r2FType = 'closure',
      } = {},
    } = {},
    valueKey,
    intl,
    test,
  } = props

  const [state, setState] = useState(() => {
    const sensors = {
      AN1: anIn1Label,
      AN2: anIn2Label,
      R1F: r1FLabel,
      R2F: r2FLabel,
    }

    const formulas = {
      AN1: anIn1Type,
      AN2: anIn2Type,
      R1F: r1FType,
      R2F: r2FType,
    }

    const stateProps = { ...props }
    delete stateProps.device
    delete stateProps.intl

    return {
      ...stateProps,
      sensor: sensors[valueKey],
      sensors,
      formula: formulas[valueKey],
      formulas,
      testValueValidation: stateProps.testValue,
      testValueInvalid: false,
      invalid: true,
    }
  })

  const updateNewRule = useCallback(() => {
    const newRule = { ...state }

    AppDispatcher.dispatch({
      action: 'update-new-rule-parameters',
      template: 'multisensor',
      ...newRule,
    })
  }, [state])

  useEffect(() => {
    updateNewRule()
  }, [updateNewRule])

  const handleChange = useCallback((field, value, callback) => {
    let valueU = value
    if (field === 'sendOnce') {
      valueU = value === 'true'
    }

    setState((prevState) => ({
      ...prevState,
      [field]: valueU,
    }))

    if (callback) callback()
  }, [])

  const handleChangeInterval = useCallback((field, value, callback) => {
    setState((prevState) => ({
      ...prevState,
      [field]: Number(value),
    }))

    if (callback) callback()
  }, [])

  const handleChangeTestValue = useCallback((field, value, callback) => {
    const valueNumber = Number(value)
    const valueCorrect =
      isFinite(parseFloat(value)) &&
      valueNumber > 0 &&
      valueNumber <= MAX_PRESSURE

    if (valueCorrect) {
      setState((prevState) => ({
        ...prevState,
        [field]: valueNumber,
        // a sensor must also be selected before the rule can be considered complete
        invalid: !prevState.valueKey,
        testValueInvalid: false,
        testValueValidation: value,
      }))
    } else {
      setState((prevState) => ({
        ...prevState,
        invalid: true,
        testValueInvalid: true,
        testValueValidation: value,
      }))
    }

    if (callback) callback()
  }, [])

  const handleChangeValueKey = useCallback((field, value, callback) => {
    const sensor = state.sensors[value]
    const formula = state.formulas[value]
    const { formatMessage } = intl

    const labelName = {
      AN1: 'anIn1Label',
      AN2: 'anIn2Label',
      R1F: 'r1FLabel',
      R2F: 'r2FLabel',
    }[value]

    let body

    // closure formulas resolve to a boolean state, not a psi reading, so there is no
    // meaningful value to interpolate into the message for those sensors.
    const hasNumericValue = formula !== 'closure'

    if (test === 'greaterThan' && hasNumericValue) {
      body = formatMessage({
        id: 'rules.pump.pressure.high.message',
        defaultMessage: '{sensor} at {deviceNumber} is HIGH. Pressure is {value}.',
      }, {
        deviceNumber: '{{deviceNumber}}',
        value: `{{${value},${formula},1}}`,
        sensor: `{{${labelName}}}`,
      })
    } else if (test === 'greaterThan') {
      body = formatMessage({
        id: 'rules.pump.pressure.high.message.noValue',
        defaultMessage: '{sensor} at {deviceNumber} is HIGH.',
      }, {
        deviceNumber: '{{deviceNumber}}',
        sensor: `{{${labelName}}}`,
      })
    }

    if (test === 'lessThan' && hasNumericValue) {
      body = formatMessage({
        id: 'rules.pump.pressure.low.message',
        defaultMessage: '{sensor} at {deviceNumber} is LOW. Pressure is {value}.',
      }, {
        deviceNumber: '{{deviceNumber}}',
        value: `{{${value},${formula},1}}`,
        sensor: `{{${labelName}}}`,
      })
    } else if (test === 'lessThan') {
      body = formatMessage({
        id: 'rules.pump.pressure.low.message.noValue',
        defaultMessage: '{sensor} at {deviceNumber} is LOW.',
      }, {
        deviceNumber: '{{deviceNumber}}',
        sensor: `{{${labelName}}}`,
      })
    }

    const testValueNumber = Number(state.testValueValidation)
    const testValueCorrect =
      isFinite(parseFloat(state.testValueValidation)) &&
      testValueNumber > 0 &&
      testValueNumber <= MAX_PRESSURE

    setState((prevState) => ({
      ...prevState,
      body,
      // the test value must also be valid before the rule can be considered complete
      invalid: !testValueCorrect,
      valueKey: value,
      sensor,
      formula,
    }))

    if (callback) callback()
  }, [state.sensors, state.formulas, state.testValueValidation, intl, test])

  const childChange = useCallback((event) => {
    const { target: { name, value } } = event

    switch (name) {
      case 'testValue':
        handleChangeTestValue(name, value)
        break

      case 'notificationInterval':
        handleChangeInterval(name, value)
        break

      case 'sendOnce':
        handleChange(event.target.name, event.target.value)
        break

      case 'valueKey':
        handleChangeValueKey(name, value)
        break

      default:
        break
    }
  }, [handleChangeTestValue, handleChangeInterval, handleChange, handleChangeValueKey])

  const translation = (id, defaultMessage) => intl.formatMessage({ id, defaultMessage })

  const {
    device: {
      physical: {
        anIn1Label: deviceAnIn1Label = translation('setting.pump.analogInput1.label', 'Analog Input 1'),
        anIn2Label: deviceAnIn2Label = translation('setting.pump.analogInput2.label', 'Analog Input 2'),
        r1FLabel: deviceR1FLabel = translation('setting.pump.relay1Feedback.label', 'Relay 1 Feedback'),
        r2FLabel: deviceR2FLabel = translation('setting.pump.relay2Feedback.label', 'Relay 2 Feedback'),
      } = {},
    } = {},
    intl: {
      formatMessage,
    },
  } = props

  const {
    sensors,
    valueKey: stateValueKey,
  } = state

  const sensorOptions = Object.entries(sensors)
    .map((entry) => new Object({
      name: entry[1],
      value: entry[0],
      selected: stateValueKey === entry[0],
    }))

  if (!stateValueKey) {
    // an explicit blank value keeps the native <select> on this placeholder instead of
    // silently falling back to the first enabled option, which would look selected without
    // ever having fired handleChangeValueKey (and without valueKey/formula ever being saved).
    sensorOptions.unshift({
      name: '',
      value: '',
      disabled: true,
      selected: true,
    })
  }

  const testDisplay = {
    greaterThan: formatMessage({
      id: 'rules.general.isGreaterThan',
      defaultMessage: 'is greater than',
    }),
    lessThan: formatMessage({
      id: 'rules.general.isLessThan',
      defaultMessage: 'is less than',
    }),
  }[test]

  const translate = (id, defaultMessage) => formatMessage({ id, defaultMessage })

  return (
    <div>
      <section>
        <FormattedMessage
          id='rules.sensor.name'
          defaultMessage='Sensor Name'
          description='Sensor Name heading'
          tagName='h5'
        />
        <div className={styles.ruleSelect} style={{ position: 'relative' }}>
          <SelectInput
            childChange={childChange}
            disabled={sensorOptions.length < 2}
            field='valueKey'
            options={sensorOptions}
            style={styles.ruleSelectInput}
            value={stateValueKey || ''}
          />
          {!stateValueKey && (
            <RedX
              style={{
                position: 'absolute',
                bottom: '8px',
                right: '4px',
                height: '24px',
                width: '24px',
              }}
            />
          )}
        </div>
        {!stateValueKey && (
          <FormattedMessage
            id='rules.pump.sensor.required'
            defaultMessage='Select a sensor to continue setting up this alert.'
            tagName='p'
          />
        )}
      </section>
      {stateValueKey && (
        <>
          <section>
            <FormattedMessage
              id='rules.pump.pressure.alert'
              defaultMessage='Alert me when pressure (psi) {testDisplay}'
              tagName='h5'
              values={{ testDisplay }}
            />
            <div className={styles.ruleDecimal} style={{ position: 'relative' }}>
              <NumberInput
                value={state.testValueValidation}
                childChange={childChange}
                onInput={childChange}
                field='testValue'
                min={1}
                max={200}
                steps={1}
                style={styles.ruleDecimalInput}
              />
              {(state.testValueInvalid || state.testValue == 0) && (
                <RedX
                  style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: navigator.userAgent.indexOf('Firefox') != -1 ? '24px' : '4px',
                    height: '24px',
                    width: '24px',
                  }}
                />
              )}
            </div>
          </section>
          <section>
            <FormattedMessage
              id='rules.snooze.name'
              defaultMessage='Snooze Alerts'
              description='Snooze Alerts heading'
              tagName='h5'
            />
            <div className={styles.ruleSelect}>
              <SelectInput
                value={state.notificationInterval}
                field='notificationInterval'
                childChange={childChange}
                options={[
                  {
                    value: 0,
                    name: formatMessage({
                      id: 'rules.reminders.never',
                      defaultMessage: 'None',
                      description: 'None option',
                    }),
                  },
                  {
                    value: 900000,
                    name: formatMessage({
                      id: 'rules.reminders.every15Min',
                      defaultMessage: '15 minutes',
                      description: '15 minutes option',
                    }),
                  },
                  {
                    value: 3600000,
                    name: formatMessage({
                      id: 'rules.reminders.every1Hrs',
                      defaultMessage: '1 hour',
                      description: '1 hour option',
                    }),
                  },
                  {
                    value: 14400000,
                    name: formatMessage({
                      id: 'rules.reminders.every4Hrs',
                      defaultMessage: '4 hours',
                      description: '4 hours option',
                    }),
                  },
                  {
                    value: 64800000,
                    name: formatMessage({
                      id: 'rules.reminders.every18Hrs',
                      defaultMessage: '18 hours',
                      description: '18 hours option',
                    }),
                  },
                  {
                    value: 86400000,
                    name: formatMessage({
                      id: 'rules.reminders.every24Hrs',
                      defaultMessage: '24 hours',
                      description: '24 hours option',
                    }),
                  },
                ]}
                style={styles.ruleSelectInput}
              />
            </div>
          </section>
          <section>
            <FormattedMessage
              id='rules.reminders.name'
              defaultMessage='Send Reminders'
              description='Send Reminders heading'
              tagName='h5'
            />
            <div className={styles.ruleSelect}>
              <SelectInput
                value={state.sendOnce}
                field='sendOnce'
                childChange={childChange}
                options={[
                  {
                    value: true,
                    name: formatMessage({
                      id: 'rules.sendOnce.off',
                      defaultMessage: 'Off',
                      description: 'Off option',
                    }),
                  },
                  {
                    value: false,
                    name: formatMessage({
                      id: 'rules.sendOnce.on',
                      defaultMessage: 'On',
                      description: 'On option',
                    }),
                  },
                ]}
                style={styles.ruleSelectInput}
              />
            </div>
          </section>
        </>
      )}
    </div>
  )
}

PumpPressure.propTypes = {
  rule: PropTypes.object,
}

export default injectIntl(PumpPressure)
